import { create, type DescMethodUnary } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  acceptLeadReferral,
  createLeadReferralPartner,
  deleteLeadReferralPartner,
  getLeadReferralSettings,
  getPipelineSummary,
  listIncomingLeadReferrals,
  listInquiries,
  listJobs,
  listLeadReferralPartners,
  listLeadReferrals,
  lookupLeadReferralCode,
  regenerateLeadReferralCode,
  rejectLeadReferral,
  sendLeadReferral,
  updateLeadReferralSettings,
  withdrawLeadReferral,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetLeadReferralSettingsRequestSchema,
  ListIncomingLeadReferralsRequestSchema,
  ListLeadReferralPartnersRequestSchema,
  ListLeadReferralsRequestSchema,
} from '@/gen/cortado/v1/referrals_pb'

/**
 * Lead referrals: send a lead to another Cortado business by its referral
 * code, and accept or reject the ones sent to you. Businesses can't find each
 * other; a code is the only way in.
 */

type QueryOptions = { enabled?: boolean }

/** How often the incoming list refreshes while the leads page is open. */
const INCOMING_REFETCH_MS = 60_000

export function useLeadReferralSettings(organizationId: string, options: QueryOptions = {}) {
  const request = useMemo(
    () => create(GetLeadReferralSettingsRequestSchema, { organizationId }),
    [organizationId],
  )
  const query = useQuery(getLeadReferralSettings, request, {
    enabled: options.enabled !== false && !!organizationId,
  })
  return {
    settings: query.data?.settings,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
  }
}

function useInvalidate() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  return (...schemas: DescMethodUnary[]) => {
    for (const schema of schemas) {
      void queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({ schema, transport, cardinality: undefined }),
      })
    }
  }
}

export function useUpdateLeadReferralSettings() {
  const invalidate = useInvalidate()
  return useMutation(updateLeadReferralSettings, {
    onSuccess: () => invalidate(getLeadReferralSettings),
  })
}

export function useRegenerateLeadReferralCode() {
  const invalidate = useInvalidate()
  return useMutation(regenerateLeadReferralCode, {
    onSuccess: () => invalidate(getLeadReferralSettings),
  })
}

/** Checks a code and returns the business behind it. Failed checks count toward an hourly limit. */
export function useLookupLeadReferralCode() {
  return useMutation(lookupLeadReferralCode)
}

export function useLeadReferralPartners(organizationId: string, options: QueryOptions = {}) {
  const request = useMemo(
    () => create(ListLeadReferralPartnersRequestSchema, { organizationId }),
    [organizationId],
  )
  const query = useQuery(listLeadReferralPartners, request, {
    enabled: options.enabled !== false && !!organizationId,
  })
  return {
    partners: query.data?.partners ?? [],
    hasLoaded: query.isSuccess || !!query.data,
  }
}

export function useCreateLeadReferralPartner() {
  const invalidate = useInvalidate()
  return useMutation(createLeadReferralPartner, {
    onSuccess: () => invalidate(listLeadReferralPartners),
  })
}

export function useDeleteLeadReferralPartner() {
  const invalidate = useInvalidate()
  return useMutation(deleteLeadReferralPartner, {
    onSuccess: () => invalidate(listLeadReferralPartners),
  })
}

/** Referrals this business sent, for one lead when inquiryId is given. */
export function useLeadReferrals(organizationId: string, inquiryId?: string, options: QueryOptions = {}) {
  const request = useMemo(
    () => create(ListLeadReferralsRequestSchema, { organizationId, inquiryId }),
    [organizationId, inquiryId],
  )
  const query = useQuery(listLeadReferrals, request, {
    enabled: options.enabled !== false && !!organizationId,
  })
  return {
    referrals: query.data?.referrals ?? [],
    hasLoaded: query.isSuccess || !!query.data,
  }
}

export function useSendLeadReferral() {
  const invalidate = useInvalidate()
  return useMutation(sendLeadReferral, {
    onSuccess: () => invalidate(listLeadReferrals),
  })
}

export function useWithdrawLeadReferral() {
  const invalidate = useInvalidate()
  return useMutation(withdrawLeadReferral, {
    onSuccess: () => invalidate(listLeadReferrals),
  })
}

/** Referrals waiting on this business, soonest deadline first. */
export function useIncomingLeadReferrals(organizationId: string, options: QueryOptions = {}) {
  const request = useMemo(
    () => create(ListIncomingLeadReferralsRequestSchema, { organizationId }),
    [organizationId],
  )
  const query = useQuery(listIncomingLeadReferrals, request, {
    enabled: options.enabled !== false && !!organizationId,
    refetchInterval: INCOMING_REFETCH_MS,
  })
  return {
    referrals: query.data?.referrals ?? [],
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Accepting creates a lead here, so the leads and pipeline lists refresh too. */
export function useAcceptLeadReferral() {
  const invalidate = useInvalidate()
  return useMutation(acceptLeadReferral, {
    onSuccess: () => invalidate(listIncomingLeadReferrals, listInquiries, listJobs, getPipelineSummary),
  })
}

export function useRejectLeadReferral() {
  const invalidate = useInvalidate()
  return useMutation(rejectLeadReferral, {
    onSuccess: () => invalidate(listIncomingLeadReferrals),
  })
}
